import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useProcessosRiscosFilters } from "../hooks/Qualidade_processos_riscos/filter";
import { useProcessosRiscosPagination } from "../hooks/Qualidade_processos_riscos/pagination";
import { ProcessoRiscosLista } from "../../domain/models/processos_riscos_lista";
import { QualidadeProcessosRiscosForm, QualidadeProcessosRiscosSchema } from "../schemas/ProcessosRiscos/qualidade_processos_riscos";
import { QualidadeProcessoRiscosCopiaForm } from "../schemas/ProcessosRiscos/qualidade_processos_riscos_copia";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";

//LoadProcessoRiscosListaListaFuncState
type LoadProcessoRiscosListaListaFuncState = {
  IdProcesso?: number;
  page?: number;
  rowsPerPage?: number;
  tipoPesquisa?: number,
  status?: number,
  pesquisa?: string | number | Date,
};

//useQualidadeProcessoRiscosListaViewModel

export const useQualidadeProcessoRiscosListaViewModel = () => {
  const {
    commons: {getAllLogAlteracaoUseCase, getAllLogAlteracaoByParentUseCase },
    reports: {},
    qualidade: { getAllListagemProcessosRiscosUseCase, deleteProcessosRiscosUseCase, createProcessoRiscosUseCase, updateProcessoRiscosUseCase, copyProcessoRiscosUseCase}
   
  } = useDependencyInjection();



  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [ProcessosRiscosModal, setProcessoRiscosModal] = useState<boolean>(false);
  const [ProcessoRiscosLista, setProcessoRiscosLista] = useState<ProcessoRiscosLista[]>([])
  const [selectedProcessosRiscos, setselectedProcessoRiscos] = useState<ProcessoRiscosLista>();
  const [ProcessosRiscosDeleteModal, setProcessosRiscosDeleteModal] = useState<boolean>(false);
  const [openCopiaModal, setOpenCopiaModal] = useState<boolean>(false);
  const {pushErrorNotification, pushInfoNotification, pushWarnNotification } = usePageLoader();
  const debouncedRef = useCreateDebounceHandler(750);

  const filters = useProcessosRiscosFilters();
  const pagination = useProcessosRiscosPagination();
  

  // #region Funções de carregamento

  const loadProcessoRiscosListaState = useCallback(async (funcState?: LoadProcessoRiscosListaListaFuncState) => {
      try {
        const filterID_IdProcesso = evaluateParamValue(funcState?.IdProcesso, filters.IdProcesso);
        const filteredPage = funcState?.page ?? pagination.page;
        const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;
        const filteredPesquisa = funcState?.pesquisa ?? filters.pesquisa;
        await getAllListagemProcessosRiscosUseCase.getAllListagemProcessosRiscos(
          {
            IdProcesso: filterID_IdProcesso, Pesquisar: String(filteredPesquisa ) 
          },
          filteredPage,
          filteredRowsPerPage
         );
        setProcessoRiscosLista(getAllListagemProcessosRiscosUseCase.items);
        pagination.setRowsPerPage(getAllListagemProcessosRiscosUseCase.pageSize);
        pagination.setTotalItems(getAllListagemProcessosRiscosUseCase.totalItems);
         
      } catch (e: unknown) {
          throw new Error("Falha ao Processo Risco Lista!");
      }
    }, [filters.IdProcesso, filters.pesquisa, getAllListagemProcessosRiscosUseCase, pagination]);


  //#endregion

  // #region useEffect
  usePageStartBootstrap(async () => {
    try {
      const jobs = [];
      jobs.push(loadProcessoRiscosListaState());
      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  const handleReload = useCallback(async () => {
    try {
      const jobs = [];
      setLoading(true);
      jobs.push(loadProcessoRiscosListaState());
      
      await Promise.all(jobs);
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadProcessoRiscosListaState, pushErrorNotification]);
  //#endregion

  // #region Eventos

 const handleProcessoRiscosSubmit = useCallback(async (form: QualidadeProcessosRiscosForm) => {
  try {
    setSaving(true);
    const anoFormatado = String(form.Ano);
    const anoJaExiste = ProcessoRiscosLista.some((item) => {
      if (selectedProcessosRiscos && item.IdProcesso === selectedProcessosRiscos.IdProcesso) {
        return false;
      }
      return item.Ano === anoFormatado;
    });

    if (anoJaExiste) {
      pushWarnNotification("Atenção", `O ano ${anoFormatado} já está cadastrado!`);
      setSaving(false);
      return; 
    }

    if (selectedProcessosRiscos) {
      await updateProcessoRiscosUseCase.updateProcessoRiscos(selectedProcessosRiscos.IdProcesso, anoFormatado);
      setProcessoRiscosModal(false);
      pushInfoNotification("Sucesso", "Processo Riscos atualizado com sucesso!", { isAutoFinish: true });
    } else {
      await createProcessoRiscosUseCase.createProcessoRiscos(anoFormatado);
      setProcessoRiscosModal(false);
      pushInfoNotification("Sucesso", "Processo Riscos criado com sucesso!", { isAutoFinish: true });
    }
    
    loadProcessoRiscosListaState();
    
  } catch (e) {
      pushErrorNotification("Erro", processError(e, 'Falha ao criar/atualizar Processo Risco!'));
  } finally {
    setSaving(false);
  }
}, [ProcessoRiscosLista, createProcessoRiscosUseCase, loadProcessoRiscosListaState, pushErrorNotification, pushInfoNotification, selectedProcessosRiscos, updateProcessoRiscosUseCase]);

  const handleCadastroProcessosRiscosOpenState = useCallback((IdProcesso: number | null, openState: boolean) => {
    if (openState) {
      if (IdProcesso) {
        setselectedProcessoRiscos(ProcessoRiscosLista.find(x => x.IdProcesso === IdProcesso));
      } else {
        setselectedProcessoRiscos(undefined);
      }
    }
    setProcessoRiscosModal(openState);
  }, [ProcessoRiscosLista]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      pagination.setPage(page + 1);;
      await loadProcessoRiscosListaState({page: page + 1,});
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadProcessoRiscosListaState, pagination, pushErrorNotification]);

  
  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
        setLoading(true);
        pagination.setPage(1);
        pagination.setRowsPerPage(rowsPerPage);
  
        await loadProcessoRiscosListaState({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadProcessoRiscosListaState, pagination, pushErrorNotification]);

   const handleDeleteProcessoRiscosModalOpenState = useCallback((IdProcesso: number | null, openState: boolean) => {
    if (openState) {
      if (IdProcesso && ProcessoRiscosLista) {
        setselectedProcessoRiscos(ProcessoRiscosLista.find(x => x.IdProcesso === IdProcesso));
      } else {
        setselectedProcessoRiscos(undefined);
      }
    }
    setProcessosRiscosDeleteModal(openState);
  }, [ProcessoRiscosLista]);
  

  const handleDeleteConfirmaProcessoRiscos = useCallback(async () => {
    if (selectedProcessosRiscos) {        
      try {
          setSaving(true);
          await deleteProcessosRiscosUseCase.deleteProcessoRiscos(selectedProcessosRiscos.IdProcesso);
          await loadProcessoRiscosListaState();
          pushInfoNotification("Sucesso", "Processo Riscos excluido com sucesso!", { isAutoFinish: true });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, 'Falha ao excluir informações!'));
        } finally {
          setSaving(false);
          setProcessosRiscosDeleteModal(false);
        }
      }
  }, [deleteProcessosRiscosUseCase, loadProcessoRiscosListaState, pushErrorNotification, pushInfoNotification, selectedProcessosRiscos]);



  //#region Copia
  
  const handleProcessoRiscosCopiaOpenState = useCallback((IdProcesso: number | null, openState: boolean) => {
    if (openState) {
        setselectedProcessoRiscos(ProcessoRiscosLista.find(x => x.IdProcesso === IdProcesso));
        setOpenCopiaModal(true);
    } else {
        setOpenCopiaModal(false);
    }
  }, [ProcessoRiscosLista]);
      
  const handleSubmitCopiaProcessoRiscos = useCallback(async (form: QualidadeProcessoRiscosCopiaForm) => {
    try {
        if (selectedProcessosRiscos) {
            setSaving(true);
            await copyProcessoRiscosUseCase.copyProcessoRiscos(selectedProcessosRiscos.IdProcesso,String(form.ano));
            await loadProcessoRiscosListaState();
            pushInfoNotification("Sucesso", "Processo Riscos copiada com sucesso!", { isAutoFinish: true });
        } 
    } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao copiar Processo Riscos!"));
    } finally {
        setOpenCopiaModal(false);
        setSaving(false);
    }
  }, [selectedProcessosRiscos, copyProcessoRiscosUseCase, loadProcessoRiscosListaState, pushInfoNotification, pushErrorNotification]);
  
    //#endregion
    

  //#endregion



  //#region Filtros
  const handleClearFilters = useCallback(async () => {
    try {
        setLoading(true);
        filters.clearFilters();
        pagination.setPage(1);
        await loadProcessoRiscosListaState();
    } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar Processo Riscos!'));
    } finally {
        setLoading(false);
    }
  }, [filters, loadProcessoRiscosListaState, pagination, pushErrorNotification]);

  const handlePesquisaChange = useDebouncedCallback( debouncedRef,  (newValue: string | null) => {
    useProcessosRiscosFilters.getState().setPesquisa(newValue);
    
    if (newValue) {
      return async () => {
        try {
          setLoading(true);
          useProcessosRiscosPagination.getState().setPage(1);
          await loadProcessoRiscosListaState({ pesquisa: newValue, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao carregar Processo Riscos!"));
        } finally {
          setLoading(false);
        }
      }
    } else {
      return async () => {
        try {
          setLoading(true);
          useProcessosRiscosPagination.getState().setPage(1);
          await loadProcessoRiscosListaState(); 
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao carregar Provisao Recursos!"));
        } finally {
          setLoading(false);
        }
      }
    }
  }, 
    [ loadProcessoRiscosListaState, pushErrorNotification, setLoading]
  );
  

  //#endregion
  const handleOpenChangeHistory = useCallback(
    (idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
      if (idTabela && tipoTabela) {
        changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
      } 
    },
    [changeHistory],
  );


  return {
      loading,
      saving,
      errorMessage,
      pagination,
      filters,
      ProcessoRiscosLista,
      ProcessosRiscosDeleteModal,
      selectedProcessosRiscos,
      ProcessosRiscosModal,
      openCopiaModal,
      changeHistory,
      handleReload,
      handleRowsPerPageChange,
      handleOpenChangeHistory,
      handlePageChange,
      handlePesquisaChange,
      handleClearFilters,
      handleDeleteProcessoRiscosModalOpenState, 
      handleDeleteConfirmaProcessoRiscos,
      handleCadastroProcessosRiscosOpenState,
      handleProcessoRiscosSubmit,
      handleSubmitCopiaProcessoRiscos,
      handleProcessoRiscosCopiaOpenState
  }
}